import { useEffect, useState } from 'react'
import { useAuth } from '../hooks/useAuth'
import { supabase } from '../lib/supabase'
import { Plus, Search, X, FileText, Edit2 } from 'lucide-react'
import { Link } from 'react-router-dom'
import { format, isPast } from 'date-fns'

const STATUS_BADGE = { unpaid: 'warning', partially_paid: 'info', paid: 'success', overdue: 'danger', disputed: 'neutral' }
const STATUSES = ['unpaid', 'partially_paid', 'paid', 'overdue', 'disputed']
const EMPTY = { supplier_id: '', po_id: '', invoice_number: '', invoice_date: '', due_date: '', amount: '', amount_paid: '0', status: 'unpaid', payment_date: '', payment_reference: '', notes: '', file_url: '' }

export default function Invoices() {
  const { activeBrand } = useAuth()
  const [invoices, setInvoices] = useState([])
  const [suppliers, setSuppliers] = useState([])
  const [pos, setPOs] = useState([])
  const [search, setSearch] = useState('')
  const [modal, setModal] = useState(false)
  const [editing, setEditing] = useState(null)
  const [form, setForm] = useState(EMPTY)
  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(true)
  const [filterStatus, setFilterStatus] = useState('')

  useEffect(() => { if (activeBrand) load() }, [activeBrand])

  async function load() {
    setLoading(true)
    const [{ data: invData }, { data: supData }, { data: poData }] = await Promise.all([
      supabase.from('invoices').select('*, suppliers(name), purchase_orders(po_number)').eq('brand_id', activeBrand.id).order('invoice_date', { ascending: false }),
      supabase.from('suppliers').select('id, name').eq('brand_id', activeBrand.id).order('name'),
      supabase.from('purchase_orders').select('id, po_number').eq('brand_id', activeBrand.id).order('po_number')
    ])
    setInvoices(invData || [])
    setSuppliers(supData || [])
    setPOs(poData || [])
    setLoading(false)
  }

  function openNew() { setEditing(null); setForm(EMPTY); setModal(true) }
  function openEdit(inv) { setEditing(inv); setForm({ ...inv, amount: inv.amount || '', amount_paid: inv.amount_paid || '0', supplier_id: inv.supplier_id || '', po_id: inv.po_id || '', invoice_date: inv.invoice_date || '', due_date: inv.due_date || '', payment_date: inv.payment_date || '' }); setModal(true) }

  async function save() {
    setSaving(true)
    const payload = {
      ...form,
      brand_id: activeBrand.id,
      supplier_id: form.supplier_id || null,
      po_id: form.po_id || null,
      amount: parseFloat(form.amount) || null,
      amount_paid: parseFloat(form.amount_paid) || 0,
      invoice_date: form.invoice_date || null,
      due_date: form.due_date || null,
      payment_date: form.payment_date || null
    }
    if (editing) await supabase.from('invoices').update(payload).eq('id', editing.id)
    else await supabase.from('invoices').insert(payload)
    setSaving(false)
    setModal(false)
    load()
  }

  const totalOutstanding = invoices.filter(i => ['unpaid', 'partially_paid', 'overdue'].includes(i.status))
    .reduce((sum, i) => sum + ((i.amount || 0) - (i.amount_paid || 0)), 0)

  let filtered = invoices.filter(i =>
    (i.invoice_number || '').toLowerCase().includes(search.toLowerCase()) ||
    (i.suppliers?.name || '').toLowerCase().includes(search.toLowerCase())
  )
  if (filterStatus) filtered = filtered.filter(i => i.status === filterStatus)

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Invoices</h1>
          <div style={{ fontSize: '13px', color: 'var(--text-3)', marginTop: '2px' }}>
            Outstanding: <span style={{ color: 'var(--warning)', fontWeight: 600 }}>${totalOutstanding.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
          </div>
        </div>
        <div className="page-header-actions">
          <div className="search-input-wrap">
            <Search size={14} />
            <input className="input" placeholder="Search invoices..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <select className="select" style={{ width: 'auto' }} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="">All Statuses</option>
            {STATUSES.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
          </select>
          <button className="btn btn-primary" onClick={openNew}><Plus size={14} />Add Invoice</button>
        </div>
      </div>

      <div className="card">
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
        ) : filtered.length === 0 ? (
          <div className="empty-state"><FileText size={40} /><p>No invoices found</p></div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Invoice #</th><th>Supplier</th><th>PO</th><th>Invoice Date</th><th>Due Date</th><th>Amount</th><th>Paid</th><th>Balance</th><th>Status</th><th></th></tr>
              </thead>
              <tbody>
                {filtered.map(inv => {
                  const balance = (inv.amount || 0) - (inv.amount_paid || 0)
                  const overdue = inv.due_date && isPast(new Date(inv.due_date)) && inv.status !== 'paid'
                  return (
                    <tr key={inv.id}>
                      <td><span className="mono" style={{ fontSize: '12px' }}>{inv.invoice_number}</span></td>
                      <td>{inv.suppliers?.name || '—'}</td>
                      <td>{inv.purchase_orders ? <Link to={`/purchase-orders/${inv.po_id}`} style={{ color: 'var(--accent)', textDecoration: 'none', fontFamily: 'var(--mono)', fontSize: '12px' }}>{inv.purchase_orders.po_number}</Link> : '—'}</td>
                      <td>{inv.invoice_date ? format(new Date(inv.invoice_date), 'MMM d, yyyy') : '—'}</td>
                      <td style={{ color: overdue ? 'var(--danger)' : 'inherit' }}>{inv.due_date ? format(new Date(inv.due_date), 'MMM d, yyyy') : '—'}</td>
                      <td>${(inv.amount || 0).toFixed(2)}</td>
                      <td style={{ color: 'var(--success)' }}>${(inv.amount_paid || 0).toFixed(2)}</td>
                      <td style={{ fontWeight: balance > 0 ? 600 : 400 }}>${balance.toFixed(2)}</td>
                      <td><span className={`badge badge-${STATUS_BADGE[inv.status] || 'neutral'}`}>{inv.status.replace(/_/g, ' ')}</span></td>
                      <td><button className="btn btn-ghost btn-sm" onClick={() => openEdit(inv)}><Edit2 size={13} /></button></td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModal(false)}>
          <div className="modal modal-lg">
            <div className="modal-header">
              <h2>{editing ? 'Edit Invoice' : 'Add Invoice'}</h2>
              <button className="btn btn-ghost btn-sm" onClick={() => setModal(false)}><X size={16} /></button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Invoice Number *</label>
                  <input className="input" value={form.invoice_number} onChange={e => setForm(f => ({ ...f, invoice_number: e.target.value }))} placeholder="INV-001" />
                </div>
                <div className="form-group">
                  <label className="form-label">Supplier</label>
                  <select className="select" value={form.supplier_id} onChange={e => setForm(f => ({ ...f, supplier_id: e.target.value }))}>
                    <option value="">— Select —</option>
                    {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Linked PO</label>
                  <select className="select" value={form.po_id} onChange={e => setForm(f => ({ ...f, po_id: e.target.value }))}>
                    <option value="">— None —</option>
                    {pos.map(p => <option key={p.id} value={p.id}>{p.po_number}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Status</label>
                  <select className="select" value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                    {STATUSES.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-grid-3">
                <div className="form-group">
                  <label className="form-label">Invoice Date</label>
                  <input className="input" type="date" value={form.invoice_date} onChange={e => setForm(f => ({ ...f, invoice_date: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Due Date</label>
                  <input className="input" type="date" value={form.due_date} onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Payment Date</label>
                  <input className="input" type="date" value={form.payment_date} onChange={e => setForm(f => ({ ...f, payment_date: e.target.value }))} />
                </div>
              </div>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Invoice Amount</label>
                  <input className="input" type="number" step="0.01" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} placeholder="0.00" />
                </div>
                <div className="form-group">
                  <label className="form-label">Amount Paid</label>
                  <input className="input" type="number" step="0.01" value={form.amount_paid} onChange={e => setForm(f => ({ ...f, amount_paid: e.target.value }))} placeholder="0.00" />
                </div>
              </div>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Payment Reference</label>
                  <input className="input" value={form.payment_reference} onChange={e => setForm(f => ({ ...f, payment_reference: e.target.value }))} placeholder="Wire ref, check #..." />
                </div>
                <div className="form-group">
                  <label className="form-label">File URL (invoice doc)</label>
                  <input className="input" value={form.file_url} onChange={e => setForm(f => ({ ...f, file_url: e.target.value }))} placeholder="https://..." />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Notes</label>
                <textarea className="textarea" rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={saving || !form.invoice_number}>
                {saving ? 'Saving...' : editing ? 'Update Invoice' : 'Add Invoice'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
